import React, { useState, useEffect } from "react";
import { useDraggable } from "@dnd-kit/core";
import { IconButton, Box } from "@mui/material";
import { Brightness4, Brightness7 } from "@mui/icons-material";
import { useTheme } from "../../contexts/ThemeContext";

// Custom GripVertical icon component
const GripVertical = ({ className, style }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={style}
  >
    <circle cx="2" cy="2" r="1.5" fill="currentColor" />
    <circle cx="2" cy="6" r="1.5" fill="currentColor" />
    <circle cx="2" cy="10" r="1.5" fill="currentColor" />
    <circle cx="6" cy="2" r="1.5" fill="currentColor" />
    <circle cx="6" cy="6" r="1.5" fill="currentColor" />
    <circle cx="6" cy="10" r="1.5" fill="currentColor" />
  </svg>
);

export default function ThemeToggle({ position }) {
  const { theme, toggleTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: "theme-toggle",
    });

  useEffect(() => {
    setMounted(true);
    // Detect touch device
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  if (!mounted) return null;

  const currentX = transform ? position.x + transform.x : position.x;
  const currentY = transform ? position.y + transform.y : position.y;

  const containerStyle = {
    transform: `translate3d(${currentX}px, ${currentY}px, 0)`,
    touchAction: "none",
    userSelect: "none",
    WebkitUserSelect: "none",
  };

  // On mobile/touch: show grip on long-press or when dragging
  // On desktop: show grip on hover
  const showGrip = isTouchDevice ? isDragging : (isHovered || isDragging);

  return (
    <Box
      ref={setNodeRef}
      style={containerStyle}
      onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
      onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {/* Drag Handle - Shows on hover (desktop) or when dragging (mobile) */}
      <Box
        {...listeners}
        {...attributes}
        sx={{
          cursor: isDragging ? "grabbing" : "grab",
          padding: showGrip ? "4px" : 0,
          width: showGrip ? "auto" : 0,
          opacity: showGrip ? (isTouchDevice ? 0.4 : 1) : 0,
          transform: showGrip ? "translateX(0)" : "translateX(-8px)",
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isTouchDevice ? "transparent" : "hsl(var(--accent) / 0.5)",
          },
        }}
      >
        <GripVertical
          style={{
            color: "var(--dark-text-muted)",
            width: "12px",
            height: "12px",
          }}
        />
      </Box>

      {/* Theme Toggle Button - Only this is clickable */}
      <IconButton
        onClick={toggleTheme}
        aria-label="Toggle theme"
        sx={{
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          backgroundColor: "var(--card-bg-color)",
          border: "1px solid var(--border-color)",
          color: "var(--text-color)",
          "&:hover": {
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            transform: "scale(1.05)",
            backgroundColor: "var(--card-bg-color)",
          },
          "&:active": {
            transform: "scale(0.95)",
          },
          transition: "all 0.2s ease",
        }}
      >
        {theme === "dark" ? (
          <Brightness7 sx={{ fontSize: "20px", color: "var(--text-color)" }} />
        ) : (
          <Brightness4 sx={{ fontSize: "20px", color: "var(--text-color)" }} />
        )}
      </IconButton>
    </Box>
  );
}
